import { useEffect, useState } from 'react';
import {
  Key,
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  Activity,
  AlertCircle,
  CheckCircle,
  X,
  Server,
  Zap,
} from 'lucide-react';

interface ApiKey {
  id: string;
  provider: 'landline' | 'tcpa' | 'ghl';
  key_identifier: string;
  api_key_masked: string;
  is_active: boolean;
  is_healthy: boolean;
  last_health_check: string | null;
  health_check_error: string | null;
  rate_limit_per_second: number;
  current_load: number;
  total_calls: number;
  total_errors: number;
  total_successes: number;
  error_rate: number;
  last_used: string | null;
  created_at: string;
}

interface ApiKeyFormData {
  provider: string;
  key_identifier: string;
  api_key: string;
  api_secret: string;
  username: string;
  password: string;
  rate_limit_per_second: number;
  is_active: boolean;
}

const emptyFormData: ApiKeyFormData = {
  provider: 'landline',
  key_identifier: '',
  api_key: '',
  api_secret: '',
  username: '',
  password: '',
  rate_limit_per_second: 50,
  is_active: true,
};

export default function AdminApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [formData, setFormData] = useState<ApiKeyFormData>(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Health check state
  const [checkingHealth, setCheckingHealth] = useState<string | null>(null);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/api-keys`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setApiKeys(data.apiKeys || []);
      } else {
        setError(data.error || 'Failed to fetch API keys');
      }
    } catch (err) {
      console.error('[ApiKeys] Error:', err);
      setError('Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData(emptyFormData);
    setEditingKey(null);
    setFormError(null);
    setShowModal(true);
  };

  const handleEdit = (key: ApiKey) => {
    setModalMode('edit');
    setEditingKey(key);
    setFormData({
      provider: key.provider,
      key_identifier: key.key_identifier,
      api_key: '', // Don't populate - user must re-enter to update
      api_secret: '',
      username: '',
      password: '',
      rate_limit_per_second: key.rate_limit_per_second,
      is_active: key.is_active,
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleDelete = async (key: ApiKey) => {
    if (!confirm(`Are you sure you want to deactivate "${key.key_identifier}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/api-keys/${key.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        fetchApiKeys();
      } else {
        alert(data.error || 'Failed to delete API key');
      }
    } catch (err) {
      console.error('[ApiKeys] Delete error:', err);
      alert('Failed to delete API key');
    }
  };

  const handleHealthCheck = async (key: ApiKey) => {
    setCheckingHealth(key.id);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/api-keys/${key.id}/health-check`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        // Refresh the list to get updated health status
        fetchApiKeys();
      } else {
        alert(data.error || 'Health check failed');
      }
    } catch (err) {
      console.error('[ApiKeys] Health check error:', err);
      alert('Failed to perform health check');
    } finally {
      setCheckingHealth(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      const url =
        modalMode === 'create'
          ? `${import.meta.env.VITE_API_URL}/admin/api-keys`
          : `${import.meta.env.VITE_API_URL}/admin/api-keys/${editingKey?.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      // Build body - only include non-empty values for update
      const body: Record<string, any> = {
        provider: formData.provider,
        key_identifier: formData.key_identifier,
        rate_limit_per_second: formData.rate_limit_per_second,
        is_active: formData.is_active,
      };

      // Only include credentials if provided
      if (formData.api_key) body.api_key = formData.api_key;
      if (formData.api_secret) body.api_secret = formData.api_secret;
      if (formData.username) body.username = formData.username;
      if (formData.password) body.password = formData.password;

      // Require api_key for create
      if (modalMode === 'create' && !formData.api_key) {
        setFormError('API Key is required');
        setSaving(false);
        return;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setShowModal(false);
        fetchApiKeys();
      } else {
        setFormError(data.error || 'Failed to save API key');
      }
    } catch (err) {
      console.error('[ApiKeys] Save error:', err);
      setFormError('Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  // Group keys by provider
  const keysByProvider = {
    landline: apiKeys.filter((k) => k.provider === 'landline'),
    tcpa: apiKeys.filter((k) => k.provider === 'tcpa'),
    ghl: apiKeys.filter((k) => k.provider === 'ghl'),
  };

  // Calculate totals
  const totalCalls = apiKeys.reduce((sum, k) => sum + k.total_calls, 0);
  const totalErrors = apiKeys.reduce((sum, k) => sum + k.total_errors, 0);
  const healthyKeys = apiKeys.filter((k) => k.is_healthy && k.is_active).length;
  const activeKeys = apiKeys.filter((k) => k.is_active).length;

  if (loading && apiKeys.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-gray-500 mt-1">Manage API keys for external services</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchApiKeys}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Key</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Keys"
          value={activeKeys}
          subtitle={`${apiKeys.length} total`}
          icon={Key}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Healthy Keys"
          value={healthyKeys}
          subtitle={`of ${activeKeys} active`}
          icon={CheckCircle}
          iconBg="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Total API Calls"
          value={totalCalls.toLocaleString()}
          icon={Activity}
          iconBg="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Total Errors"
          value={totalErrors.toLocaleString()}
          subtitle={
            totalCalls > 0
              ? `${((totalErrors / totalCalls) * 100).toFixed(2)}% error rate`
              : undefined
          }
          icon={AlertCircle}
          iconBg="bg-red-100"
          iconColor="text-red-600"
        />
      </div>

      {/* Keys by Provider */}
      {(['landline', 'tcpa', 'ghl'] as const).map((provider) => {
        const keys = keysByProvider[provider];
        if (keys.length === 0) return null;

        return (
          <div
            key={provider}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-3">
                <ProviderIcon provider={provider} />
                <h2 className="text-lg font-semibold text-gray-900 capitalize">{provider} API</h2>
                <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded-full">
                  {keys.length} key{keys.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Identifier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate Limit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Used
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {keys.map((key) => (
                    <tr key={key.id} className={`hover:bg-gray-50 ${!key.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{key.key_identifier}</p>
                          <p className="text-xs text-gray-500 font-mono">{key.api_key_masked}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              key.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {key.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              key.is_healthy
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {key.is_healthy ? 'Healthy' : 'Unhealthy'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {key.total_calls.toLocaleString()} calls
                          </p>
                          <p className="text-xs text-gray-500">
                            {key.error_rate.toFixed(2)}% error rate
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {key.rate_limit_per_second}/sec
                          </p>
                          <p className="text-xs text-gray-500">Load: {key.current_load}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900">
                          {key.last_used ? formatTimeAgo(key.last_used) : 'Never'}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleHealthCheck(key)}
                            disabled={checkingHealth === key.id}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Run health check"
                          >
                            {checkingHealth === key.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Zap className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEdit(key)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(key)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {apiKeys.length === 0 && !loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys</h3>
          <p className="text-gray-500 mb-4">Get started by adding your first API key.</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add API Key
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowModal(false)}
            />

            <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalMode === 'create' ? 'Add API Key' : 'Edit API Key'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <select
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    disabled={modalMode === 'edit'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="landline">Landline API</option>
                    <option value="tcpa">TCPA Litigator API</option>
                    <option value="ghl">GoHighLevel API</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key Identifier
                  </label>
                  <input
                    type="text"
                    value={formData.key_identifier}
                    onChange={(e) => setFormData({ ...formData, key_identifier: e.target.value })}
                    placeholder="e.g., Primary Key, Backup Key"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key {modalMode === 'edit' && '(leave blank to keep existing)'}
                  </label>
                  <input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder={modalMode === 'edit' ? '••••••••' : 'Enter API key'}
                    required={modalMode === 'create'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {(formData.provider === 'tcpa' || formData.provider === 'ghl') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        placeholder="Enter username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password (optional)
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate Limit (per second)
                  </label>
                  <input
                    type="number"
                    value={formData.rate_limit_per_second}
                    onChange={(e) =>
                      setFormData({ ...formData, rate_limit_per_second: parseInt(e.target.value) || 50 })
                    }
                    min="1"
                    max="1000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconBg,
  iconColor,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: typeof Key;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

// Provider Icon Component
function ProviderIcon({ provider }: { provider: string }) {
  const getStyles = () => {
    switch (provider) {
      case 'landline':
        return { bg: 'bg-orange-100', color: 'text-orange-600' };
      case 'tcpa':
        return { bg: 'bg-red-100', color: 'text-red-600' };
      case 'ghl':
        return { bg: 'bg-blue-100', color: 'text-blue-600' };
      default:
        return { bg: 'bg-gray-100', color: 'text-gray-600' };
    }
  };

  const styles = getStyles();

  return (
    <div className={`${styles.bg} p-2 rounded-lg`}>
      <Server className={`w-5 h-5 ${styles.color}`} />
    </div>
  );
}

// Time formatting helper
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}
